import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import API from "../utils/api";
import useLocalStorage from "../hooks/useLocalStorage";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { isSessionEnded } from "../utils/sessionEnded";

const PROD_READ_ONLY_PERMISSION = "app_PremierPredict_ReadOnly";
const TEST_READ_ONLY_PERMISSION = "app_PremierPredictTest_ReadOnly";
const DEV_READ_ONLY_PERMISSION = "app_PremierPredictDev_ReadOnly";
const DEV_ENVIRONMENT = "dev.";
const TEST_ENVIRONMENT = "test.";

const UserContext = createContext();
const graphScopes = ["User.Read", "GroupMember.Read.All"];

function getReadOnlyPermission() {
  const origin = window?.location?.origin ?? "";
  if (origin.includes(DEV_ENVIRONMENT)) return DEV_READ_ONLY_PERMISSION;
  if (origin.includes(TEST_ENVIRONMENT)) return TEST_READ_ONLY_PERMISSION;
  return PROD_READ_ONLY_PERMISSION;
}

export function useUser() {
  return useContext(UserContext);
}

export function UserProvider({ children }) {
  const [user, setUser] = useLocalStorage("user", null);
  const [urlStudyId, setURLStudyId] = useState(null);
  const [updateNavigation, setUpdateNavigation] = useState("");
  
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isReadOnlyUser = user?.groups?.includes(getReadOnlyPermission());
  
  // Prevents multiple simultaneous auth attempts on mount
  const hasStartedAuth = useRef(false);

  const fetchUserGroupNames = async (graphAccessToken) => {
    const headers = { Authorization: `Bearer ${graphAccessToken}` };
    let url = "https://graph.microsoft.com/v1.0/me/memberOf?$select=id,displayName&$top=100";
    const allGroups = [];

    while (url) {
      const res = await fetch(url, { headers });

      if (!res.ok) {
        const bodyText = await res.text();
        throw new Error(`Graph call failed (${res.status} ${res.statusText}): ${bodyText}`);
      }

      const data = await res.json();

      const groups = (data.value || [])
        .filter((x) => typeof x.displayName === "string" && x.displayName.length)
        .map((x) => x.displayName);

      allGroups.push(...groups);

      // Follow the next page if one exists, otherwise stop
      url = data["@odata.nextLink"] ?? null;
    }

    return allGroups;
  };

  useEffect(() => {
    const handleAuthentication = async () => {
      if (isSessionEnded()) {
        return;
      }
      // Don't do anything if MSAL is currently in the middle of a redirect or login
      if (inProgress !== InteractionStatus.None) return;
      if (hasStartedAuth.current) return;

      try {
        if (isAuthenticated && accounts.length > 0) {
          hasStartedAuth.current = true;
          const account = accounts[0];

          // We ALWAYS attempt to acquire a token silently. 
          // This ensures the MSAL session is actually still valid.
          const tokenResp = await instance.acquireTokenSilent({
            scopes: graphScopes,
            account,
          });

          // Sync user data if no local user exists or if the account has changed
          if (!user || user.userId !== account.localAccountId) {
            await API.signIn();

            let groups = [];
            try {
              groups = await fetchUserGroupNames(tokenResp.accessToken);
            } catch (graphErr) {
              console.error("Failed to fetch groups:", graphErr);
            }

            const nameParts = (account.name || "").split(" ");
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(" ") || "";

            const userData = {
              firstName,
              lastName,
              email: account.username,
              username: account.username,
              userId: account.localAccountId,
              locale: navigator.language || "en-US",
              name: account.name,
              groups,
            };

            setUser(userData);

            await API.saveUserLog({
              FirstName: firstName,
              LastName: lastName,
              Email: account.username,
              Username: account.username,
              UserID: account.localAccountId,
              Locale: navigator.language || "en-US",
            });
          }
        } else if (!isAuthenticated && user) {
          // If the MSAL state is unauthenticated but we have a user in localStorage, 
          // clear it to prevent "ghost" sessions.
          setUser(null);
        }
      } catch (error) {
        console.error("Authentication verification failed:", error);
        // If silent token fails, clear the local user so the app re-triggers login
        setUser(null);
        hasStartedAuth.current = false;
      }
    };

    handleAuthentication();
    // eslint-disable-next-line
  }, [isAuthenticated, accounts, inProgress]);

  return (
    <UserContext.Provider
      value={{
        isReadOnlyUser,
        user,
        setUser,
        setURLStudyId,
        urlStudyId,
        updateNavigation,
        setUpdateNavigation,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}